import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { cn } from "@fieldwork/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { NotConnected, useServer } from "@/components/server-hooks";
import { SectionTitle, ServerPage } from "@/components/server-shell";
import { Chip } from "@/components/ui-bits";
import { useAgentAction, useAgentQuery } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/questions")({
  component: Questions,
});

type Q = { line: string; text: string; answers: string[] };
type List = { open: Q[]; answered: Q[]; pending: string[] };

/** "today", "yesterday", "Thu" or "3 Oct" for an answer's date (YYYY-MM-DD). */
function day(iso: string) {
  const d = new Date(`${iso}T00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

function QuestionsBody() {
  const { id } = Route.useParams();
  const connected = !!useServer(id).data?.hub.connected;
  const q = useAgentQuery<List>(id, "questions_list", {}, connected);
  if (!connected) return <NotConnected />;
  if (q.error) return <p className="text-sm text-[#F48A80]">{q.error.message}</p>;
  if (!q.data) return <p className="text-sm text-[#A8A59D]">Loading…</p>;
  const open = q.data.open.filter((x) => !q.data!.pending.includes(x.line));
  const waiting = q.data.open.filter((x) => q.data!.pending.includes(x.line));

  return (
    <div className="flex flex-col gap-4">
      <SectionTitle count={open.length}>Waiting for you</SectionTitle>
      {open.length === 0 && <p className="m-0 text-sm text-[#A8A59D]">No questions right now.</p>}
      {open.map((x, i) => (
        <Ask key={x.line} id={id} q={x} first={i === 0} />
      ))}

      {(waiting.length > 0 || q.data.answered.length > 0) && (
        <>
          <h2 className="m-0 mt-4 font-mono text-[15px] font-bold">Answered</h2>
          <div className="flex flex-col border-b border-[#262523]">
            {waiting.map((x) => (
              <Row key={x.line} q={x} status={<Chip value="warn" label="saving…" />} />
            ))}
            {[...q.data.answered].reverse().map((x) => {
              const ignored = x.answers.some((a) => /:\s*ignore\b/i.test(a));
              return (
                <Row key={x.line} q={x} status={<Chip value={ignored ? "neutral" : "ok"} label={ignored ? "ignored" : "answered"} />} />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ q, status }: { q: Q; status: ReactNode }) {
  const last = q.answers[q.answers.length - 1] ?? "";
  const answer = last.replace(/^answer \([^)]*\):\s*/, "");
  const date = last.match(/^answer \([^)]*?(\d{4}-\d{2}-\d{2})\)/)?.[1];
  return (
    <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1.5 border-t border-[#262523] py-3.5">
      {status}
      <span className="flex-[1_1_300px]">
        <span className="font-medium">{q.text}</span>
        {answer && <span className="text-[#A8A59D]"> · “{answer}”</span>}
      </span>
      {date && <span className="text-[13px] text-[#A8A59D]">{day(date)}</span>}
    </div>
  );
}

function Ask({ id, q, first }: { id: string; q: Q; first?: boolean }) {
  const [text, setText] = useState("");
  const answer = useAgentAction<{ text?: string; ignore?: boolean }>(id, "question_answer", (v) => ({ ...v, question: q.line }), {
    refresh: ["questions_list", "people_list"],
    success: "Saved; the agent uses it on its next run",
  });
  const who = /^who\b/i.test(q.text);
  return (
    <form
      className={cn(
        "flex flex-col gap-3.5 rounded-md bg-[#1A1A19] px-[22px] py-5",
        first ? "shadow-[inset_0_0_0_1px_#3A3935]" : "shadow-[inset_0_0_0_1px_#2A2927]",
      )}
      onSubmit={(e) => {
        e.preventDefault();
        answer.mutate({ text });
      }}
    >
      <p className="m-0 text-base">{q.text}</p>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">{who ? "Who they are" : "Your answer"}</span>
        <Input
          className="h-11 bg-[#111111]"
          maxLength={400}
          placeholder={who ? 'e.g. "Sam, CTO at Acme; we work on the payments integration"' : 'e.g. "yes, the same person"'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          required
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={answer.isPending}>
          Answer
        </Button>
        <Button type="button" variant="outline" disabled={answer.isPending} onClick={() => answer.mutate({ ignore: true })}>
          Not relevant, ignore
        </Button>
      </div>
    </form>
  );
}

function Questions() {
  return (
    <ServerPage
      narrow
      title="Questions"
      subtitle={
        <span className="block max-w-[70ch]">
          People the agent couldn’t place. Your answer is saved in your notes; on its next run the agent adds them to People as confirmed by
          you, or stops tracking them if you ignore.
        </span>
      }
    >
      <QuestionsBody />
    </ServerPage>
  );
}
